import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, FileText, ExternalLink, Ban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getWallet, getInvoices, getPayments, cancelSubscription, openBillingPortal, formatApiError } from "@/lib/api";
import { BillingProfileForm } from "@/pages/DatiFatturazione";

const DOC_STATUS = {
  to_generate: ["In attesa di emissione", "text-amber-300"], generated: ["Generata · in trasmissione", "text-cyan-300"], to_send: ["In trasmissione allo SDI", "text-amber-300"],
  sent: ["Trasmessa allo SDI", "text-emerald-300"], delivered: ["Consegnata", "text-emerald-300"], rejected: ["Scartata · in gestione", "text-rose-300"], error: ["Errore · nuovo tentativo automatico", "text-rose-300"],
};
const PAY_STATUS = { initiated: "In attesa", paid: "Pagato", failed: "Non riuscito", expired: "Scaduto", refunded: "Rimborsato" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

export function BillingSection() {
  const [wallet, setWallet] = useState(null);
  const [docs, setDocs] = useState([]);
  const [pays, setPays] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => {
    getWallet().then(setWallet).catch(() => {});
    getInvoices().then((r) => setDocs(r.items)).catch(() => {});
    getPayments().then((r) => setPays(r.items)).catch(() => {});
  };
  useEffect(load, []);
  const b = wallet?.billing;
  const cancel = async () => {
    if (!window.confirm("Disdire l'abbonamento? Resta attivo fino alla fine del periodo già pagato, poi non si rinnova.")) return;
    setBusy("cancel");
    try { const r = await cancelSubscription(); toast.success(`Disdetta registrata: il piano resta attivo fino al ${fmtDate(r.ends_at)}`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const r = await openBillingPortal(); window.location.href = r.url; } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };
  return (
    <div className="space-y-6" data-testid="billing-section">
      <div className="nadia-card p-5 space-y-3">
        <div className="flex items-center gap-2 font-heading text-base font-semibold text-slate-100"><CreditCard size={16} className="text-cyan-300" /> Abbonamento</div>
        {b ? (
          <div className="text-sm text-slate-300 space-y-1" data-testid="subscription-info">
            <div>Piano <span className="font-semibold text-white">{b.plan_name}</span> · stato <span className={b.status === "active" ? "text-emerald-300" : "text-amber-300"} data-testid="subscription-status">{b.status === "active" ? "attivo" : b.status === "past_due" ? "pagamento in sospeso" : b.status === "canceled" ? "disdetto" : b.status}</span></div>
            {!b.is_flex && b.renewal_date && <div className="text-xs text-slate-400">{b.cancel_at_period_end ? "Termina il" : "Prossimo rinnovo"} {fmtDate(b.renewal_date)} · {b.renewal_mode === "stripe" ? "rinnovo gestito da Stripe" : "rinnovo interno"}</div>}
            {b.is_flex && <div className="text-xs text-slate-400">Nadia Flex: nessun rinnovo automatico.</div>}
            <div className="flex flex-wrap gap-2 pt-2">
              {!b.is_flex && b.status !== "canceled" && !b.cancel_at_period_end && (
                <Button size="sm" variant="outline" onClick={cancel} disabled={!!busy} className="border-rose-500/30 text-rose-200 hover:bg-rose-500/10" data-testid="subscription-cancel">{busy === "cancel" ? <Loader2 size={13} className="animate-spin mr-1" /> : <Ban size={13} className="mr-1" />} Disdici abbonamento</Button>
              )}
              <Button size="sm" variant="outline" onClick={portal} disabled={!!busy} className="border-white/10" data-testid="subscription-portal">{busy === "portal" ? <Loader2 size={13} className="animate-spin mr-1" /> : <ExternalLink size={13} className="mr-1" />} Metodo di pagamento (Stripe)</Button>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400" data-testid="subscription-none">Nessun piano attivo. Scegli Start o Pro nella pagina Piani.</div>}
      </div>

      <div className="nadia-card p-5 space-y-3">
        <div className="flex items-center gap-2 font-heading text-base font-semibold text-slate-100"><FileText size={16} className="text-cyan-300" /> Fatture</div>
        {docs.length === 0 ? <div className="text-sm text-slate-400" data-testid="invoices-empty">Nessuna fattura ancora. Le fatture vengono emesse automaticamente dopo ogni pagamento confermato.</div> : (
          <div className="divide-y divide-white/5" data-testid="invoices-list">
            {docs.map((d) => (
              <div key={d.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`invoice-${d.id}`}>
                <div className="min-w-0"><div className="text-slate-200 truncate">{d.label}</div><div className="text-[11px] text-slate-500">{fmtDate(d.created_at)} · {d.number ? `n. ${d.number}` : "numero in assegnazione"}</div></div>
                <div className="text-right shrink-0"><div className="text-slate-100">€ {Number(d.amount_total ?? d.amount_net).toFixed(2)}{d.amount_total == null ? " + IVA" : ""}</div><div className={`text-[11px] ${DOC_STATUS[d.status]?.[1] || "text-slate-400"}`}>{DOC_STATUS[d.status]?.[0] || d.status}</div></div>
              </div>
            ))}
          </div>
        )}
        {pays.length > 0 && (
          <details className="text-xs text-slate-400"><summary className="cursor-pointer">Storico pagamenti ({pays.length})</summary>
            <div className="mt-2 divide-y divide-white/5" data-testid="payments-list">{pays.map((p) => <div key={p.id} className="py-1.5 flex justify-between"><span>{fmtDate(p.at)} · {p.label}</span><span>€ {Number(p.amount).toFixed(2)} · {PAY_STATUS[p.status] || p.status}</span></div>)}</div>
          </details>
        )}
      </div>

      <div className="nadia-card p-5 space-y-3">
        <div className="font-heading text-base font-semibold text-slate-100">Dati di fatturazione</div>
        <BillingProfileForm compact />
      </div>
    </div>
  );
}
